export enum LogLevel {
  debug,
  info,
  warn,
  error,
}

type LogEntry = {
  timestamp: Date
  prefix: string
  level: LogLevel
  arguments: unknown[]
}

export type ChatLogger = {
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

const MAX_LOG_ENTRIES = 100
export class ChatLoggerFactory {
  private readonly level: LogLevel
  private logs: LogEntry[]

  constructor(level: LogLevel) {
    this.level = level
    this.logs = []
  }

  private appendLog(entry: LogEntry) {
    this.logs.push(entry)
    if (this.logs.length > MAX_LOG_ENTRIES) {
      this.logs.shift()
    }
  }

  private getMessagePrefix = (
    prefix: string,
    timestamp: Date,
    level: LogLevel
  ) => {
    const formattedPrefix = prefix ? `[${prefix}]` : ''
    return `[${
      LogLevel[level]
    }]${formattedPrefix}[${timestamp.toLocaleString()}] `
  }

  private log = (args: unknown[], level = LogLevel.info, prefix = '') => {
    const timestamp = new Date()
    const messagePrefix = this.getMessagePrefix(prefix, timestamp, level)
    if (this.level <= level) {
      const logMethod = console[LogLevel[level]]
      logMethod(messagePrefix, ...args)
    }
    this.appendLog({
      arguments: args,
      level,
      prefix,
      timestamp,
    })
  }

  createLogger = (prefix: string) => {
    const debug = (...args: unknown[]) => {
      this.log(args, LogLevel.debug, prefix)
    }
    const info = (...args: unknown[]) => {
      this.log(args, LogLevel.info, prefix)
    }
    const warn = (...args: unknown[]) => {
      this.log(args, LogLevel.warn, prefix)
    }
    const error = (...args: unknown[]) => {
      this.log(args, LogLevel.error, prefix)
    }

    return {
      debug,
      info,
      warn,
      error,
    }
  }

  dumpLogs = () => {
    for (const logEntry of this.logs) {
      const messagePrefix = this.getMessagePrefix(
        logEntry.prefix,
        logEntry.timestamp,
        logEntry.level
      )
      const logMethod = console[LogLevel[logEntry.level]]
      logMethod(messagePrefix, ...logEntry.arguments)
    }
  }
}

export const chatLoggerFactory = new ChatLoggerFactory(LogLevel.warn)
global.chatLoggerFactory = chatLoggerFactory
